// Client-safe fetch wrappers for the auctions API. No 'server-only' — imported by hooks.
import type { AuctionDetail } from '@auctogon/core-admin/schemas/responses/auction';

import { api } from './typed-client';
import { nullOnNotFound, recordReadFromEnvelope } from './record-read';
import type { AuctionListItem } from '@auctogon/admin-isomorphic/types/auction';
import { AUCTION_LIST_LIMIT_BOUNDS } from '@auctogon/admin-isomorphic/isomorphic/lists/auction-list-query';
import type { AuctionListCounts, AuctionListPage } from '@auctogon/core-admin/schemas/responses/auction-list';

// ─────────────────────────────────────────────────────────────────────────────
// Domain types (previously in supabase-auctions.ts)
// ─────────────────────────────────────────────────────────────────────────────

// BL-1285: the admin `AuctionStatus` union (the legacy 6-value `auction_status`
// compat label) is GONE. Admin surfaces speak the canonical ADR-023
// `AuctionPhaseKind` — import it from `@auctogon/core/isomorphic/lifecycle`.
//
// There is intentionally no `closing` phase: the close transition (ADR023
// BL-661) is thin and re-runnable — it stamps `closed_at` in one finalize write
// and leaves the auction in post-sale/active throughout, with no in-flight lock
// state at all. (BL-717 retired the pre-BL-661 `closing_started_at` mutex + its
// Resume-close banner entirely, alongside the column.)
//
// The legacy 6-value auction_status label is GONE: BL-1444 deleted its only
// consumer (`apps/admin-catalog/` and the `/api/auth/mobile/**` namespace) along
// with the derivation itself, and BL-1584 renamed the vocabulary it had lent to
// its neighbours. `AuctionPhaseKind` is the only auction phase vocabulary now.

// ADR 027 / BL-901: `Auction` (and its `AuctionType`) are the single-source
// response DTO relocated to `@auctogon/core-admin/schemas/responses/auction`.
// This file previously re-declared a divergent `Auction` twin; it's deleted —
// re-exported here so the fetch wrappers and their consumers keep importing
// `Auction` / `AuctionType` from this module. The registry now threads
// `responseType<Auction>()` / `responseType<AuctionListItem[]>()`, so the
// wrappers infer the shape with no caller-asserted `<T>`.
import type { Auction, AuctionType } from '@auctogon/admin-isomorphic/types/auction';
import type { AuctionChromeDto } from '@auctogon/core-admin/schemas/responses/auction-chrome';
import type { AuctionSettingsReadinessWriteBody } from '@auctogon/core-admin/schemas/auction-settings-readiness';
export type { Auction, AuctionType };

// BL-2177: the `AuctionLot` / `AuctionRing` twins that sat here were DELETED —
// zero importers, and they still typed the start price as a bare number. The
// Sequencing row type is `AuctionLot` in `hooks/useAuctionRings.ts`.

// ─────────────────────────────────────────────────────────────────────────────
// HTTP wrapper functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The URL-shaped query the Auctions list sends: every facet, the sort and the page window, as the
 * server's one reader (`readAuctionListQuery`) names them. Absent keys are left off the wire.
 */
export type AuctionListParams = Partial<
  Record<'search' | 'status' | 'salesRepId' | 'dateField' | 'dateFrom' | 'dateTo' | 'sortColumn' | 'sortDirection' | 'limit' | 'offset', string>
>;

/**
 * Fetch one page of the tenant's auctions (BL-2769: 50 a page, with the search, Status, Sales Rep and
 * date facets and the sort run on the server).
 *
 * @param input.tenantId - The tenant to list; none reads as an empty page.
 * @param input.params - The list's query.
 * @param input.signal - Aborts the read (a picker's superseded search, BL-2896).
 * @returns The page's rows and the total the filters match.
 */
export async function fetchAuctionsPage(input: {
  tenantId?: string | null;
  params?: AuctionListParams;
  signal?: AbortSignal;
}): Promise<AuctionListPage> {
  if (!input.tenantId) return { items: [], total: 0 };
  // ADR 027 / BL-901: the /auctions index returns `AuctionListPage` (threaded via
  // `responseType<AuctionListPage>()`); the client infers it — no `<T>`.
  const res = await api.auctions.list.get({ query: { tenantId: input.tenantId, ...input.params }, init: { signal: input.signal } });
  if (!res.success) throw new Error(res.error ?? 'Failed to load auctions');
  return res.data ?? { items: [], total: 0 };
}

/**
 * Fetch the Status pill's per-status counts under the list's other facets.
 *
 * @param input.tenantId - The tenant to count; none reads as no counts.
 * @param input.params - The list's query (its paging and sort are ignored).
 * @returns The count per status key.
 */
export async function fetchAuctionCounts(input: { tenantId?: string | null; params?: AuctionListParams }): Promise<AuctionListCounts> {
  if (!input.tenantId) return {};
  const res = await api.auctions.counts.get({ query: { tenantId: input.tenantId, ...input.params } });
  if (!res.success) throw new Error(res.error ?? 'Failed to load auction counts');
  return res.data ?? {};
}

/** The page size the "every auction" readers ask for: the route's cap, so a walk is as few reads as it can be. */
const EVERY_AUCTION_PAGE_SIZE = AUCTION_LIST_LIMIT_BOUNDS.maxLimit;

/**
 * Read every auction by walking the paged list until `total` rows are in. For the callers that need
 * the whole set (a picker, the offline sync, the "Save offline" filter); a tenant has dozens, so this
 * is one request in practice.
 *
 * @param readPage - Reads one page at a row offset and limit, over whichever typed client the caller holds.
 * @returns Every auction row.
 */
export async function collectEveryAuction(
  readPage: (window: { offset: number; limit: number }) => Promise<AuctionListPage>,
): Promise<AuctionListItem[]> {
  const rows: AuctionListItem[] = [];
  for (;;) {
    const page = await readPage({ offset: rows.length, limit: EVERY_AUCTION_PAGE_SIZE });
    rows.push(...page.items);
    // An empty page ends the walk even if `total` was stale, so a shrinking list never loops.
    if (page.items.length === 0 || rows.length >= page.total) return rows;
  }
}

/**
 * Fetch every auction for a tenant (the relist picker, the Storage sheet, the "Save offline" filter).
 *
 * @param tenantId - The tenant; none reads as empty.
 * @returns Every live auction row, newest first.
 */
export async function fetchEveryAuction(tenantId?: string | null): Promise<AuctionListItem[]> {
  return collectEveryAuction(({ offset, limit }) =>
    fetchAuctionsPage({ tenantId, params: { offset: String(offset), limit: String(limit) } }),
  );
}

/**
 * Fetch the auction-detail layout's chrome — heading, derived phase, format,
 * date line, location label, pending badge count, liveSafe (BL-1688). The
 * narrow read for the header/tab strip; segments use `fetchAuctionById`.
 *
 * Same BUG-1257 contract as `fetchAuctionById`: `null` on a real 404, throws
 * on anything else, so the layout can tell "deleted" from "couldn't load".
 */
export async function fetchAuctionChrome(id: string): Promise<AuctionChromeDto | null> {
  return nullOnNotFound(recordReadFromEnvelope(await api.auctions.id.chrome.get.withStatus({ params: { id } })));
}

/**
 * Fetch a single auction by ID, with full joins — plus the auction's currency
 * (BL-1918), which the detail read alone carries.
 */
export async function fetchAuctionById(
  id: string,
  options: { signal?: AbortSignal } = {},
): Promise<AuctionDetail | null> {
  return nullOnNotFound(
    recordReadFromEnvelope(
      await api.auctions.id.detail.get.withStatus({ params: { id }, init: { signal: options.signal } }),
    ),
  );
}

/**
 * Fetch the auctions list's filter-dropdown options — the active sales reps
 * (BL-752). The category list that used to ride beside them left with
 * `auctions.category` (BL-1775).
 */
export async function fetchAuctionFilterOptions(
  tenantId?: string | null,
): Promise<{ salesReps: Array<{ id: string; name: string }> }> {
  if (!tenantId) return { salesReps: [] };
  // ADR 027 / BL-855: filter_options carries a responseType<AuctionFilterOptionsDto>()
  // marker, so the client infers the response (no <T>).
  const res = await api.auctions.filter_options.get({
    query: { tenantId },
  });
  if (!res.success) throw new Error(res.error ?? 'Failed to load filter options');
  return { salesReps: res.data?.salesReps ?? [] };
}

/**
 * Create a new auction.
 *
 * `termsVersionId` is a create-only input, not an `Auction` field — the auction
 * carries a POINTER (`currentTermsVersionId`), and this is the published
 * version the operator picked to stamp it with (BL-1312). Optional: omitting it
 * creates an auction with no terms, which cannot go live until some are
 * attached.
 *
 * @sideEffect Inserts into `auctions` and `auction_sellers`; stamps
 *   `auctions.current_terms_version_id` when `termsVersionId` is supplied.
 */
export async function createAuction(
  // The create body names its sellers by id / name / number only. It is a
  // REQUEST shape, so it no longer borrows the response DTO, which carries no
  // resolved name (BL-2434, ADR 058). The server ignores create-time sellers
  // (BL-1013).
  auctionData: Omit<Partial<Auction>, 'sellers'> & {
    sellers?: Array<{ id: string; name: string; number: string }>;
    termsVersionId?: string;
  },
  tenantId?: string | null,
): Promise<{ data: Auction | null; warning?: string }> {
  // ADR 027 / BL-901: create returns the single-source `Auction` (threaded via
  // `responseType<Auction>()`); the client infers it — no `<T>`.
  const res = await api.auctions.list.post({ body: { ...auctionData, tenantId } });
  if (!res.success) throw new Error(res.error ?? 'Failed to create auction');
  return { data: res.data ?? null, warning: res.warning };
}

/**
 * The auction Settings index's ONE read (BL-1669 AC 2): the eight area
 * summary lines composed server-side plus the current phase. The registry's
 * `responseType<AuctionSettingsSummaryDto>()` marker types the response — no
 * caller-asserted `<T>` (ADR 027).
 *
 * @param auctionId - The auction.
 * @returns The summary DTO.
 * @throws When the server returns an error envelope or no data.
 */
export async function fetchAuctionSettingsSummary(auctionId: string) {
  const res = await api.auctions.id.settings_summary.get({ params: { id: auctionId } });
  if (!res.success) throw new Error(res.error ?? 'Failed to load auction settings');
  if (!res.data) throw new Error('Server returned success without data');
  return res.data;
}

/**
 * Tick or untick one area's "Ready?" box on the auction Settings index
 * (BL-1670). Returns the auction's FULL readiness list after the write — the
 * caller adopts it wholesale rather than flipping the box optimistically.
 *
 * @param auctionId - The auction.
 * @param body - `{ area, ready }`.
 * @returns The echoed `AuctionSettingsReadinessDto`.
 * @sideEffect INSERT or hard DELETE on `auction_settings_readiness`.
 */
export async function putAuctionSettingsReadiness(
  auctionId: string,
  body: AuctionSettingsReadinessWriteBody,
) {
  const res = await api.auctions.id.settings_readiness.put({ params: { id: auctionId }, body });
  if (!res.success) throw new Error(res.error ?? 'Failed to update readiness');
  if (!res.data) throw new Error('Server returned success without data');
  return res.data;
}

// Archive and restore have no wrapper here: the status dropdown drives both
// through `POST /api/auctions/:id/transition` (`soft-delete` / `restore`).


